import type { AutocompleteOption } from "@porchpass/ui";
import { differenceInMonths } from "date-fns";

export const MONTH_OPTIONS: AutocompleteOption[] = [
  {
    value: "01",
    label: "January",
    renderOption: (
      <>
        <span className="font-medium">January</span>{" "}
        <span className="text-on-surface-muted">- 01</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">January</span>{" "}
        <span className="text-on-surface-muted">- 01</span>
      </>
    ),
  },
  {
    value: "02",
    label: "February",
    renderOption: (
      <>
        <span className="font-medium">February</span>{" "}
        <span className="text-on-surface-muted">- 02</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">February</span>{" "}
        <span className="text-on-surface-muted">- 02</span>
      </>
    ),
  },
  {
    value: "03",
    label: "March",
    renderOption: (
      <>
        <span className="font-medium">March</span>{" "}
        <span className="text-on-surface-muted">- 03</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">March</span>{" "}
        <span className="text-on-surface-muted">- 03</span>
      </>
    ),
  },
  {
    value: "04",
    label: "April",
    renderOption: (
      <>
        <span className="font-medium">April</span>{" "}
        <span className="text-on-surface-muted">- 04</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">April</span>{" "}
        <span className="text-on-surface-muted">- 04</span>
      </>
    ),
  },
  {
    value: "05",
    label: "May",
    renderOption: (
      <>
        <span className="font-medium">May</span>{" "}
        <span className="text-on-surface-muted">- 05</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">May</span>{" "}
        <span className="text-on-surface-muted">- 05</span>
      </>
    ),
  },
  {
    value: "06",
    label: "June",
    renderOption: (
      <>
        <span className="font-medium">June</span>{" "}
        <span className="text-on-surface-muted">- 06</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">June</span>{" "}
        <span className="text-on-surface-muted">- 06</span>
      </>
    ),
  },
  {
    value: "07",
    label: "July",
    renderOption: (
      <>
        <span className="font-medium">July</span>{" "}
        <span className="text-on-surface-muted">- 07</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">July</span>{" "}
        <span className="text-on-surface-muted">- 07</span>
      </>
    ),
  },
  {
    value: "08",
    label: "August",
    renderOption: (
      <>
        <span className="font-medium">August</span>{" "}
        <span className="text-on-surface-muted">- 08</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">August</span>{" "}
        <span className="text-on-surface-muted">- 08</span>
      </>
    ),
  },
  {
    value: "09",
    label: "September",
    renderOption: (
      <>
        <span className="font-medium">September</span>{" "}
        <span className="text-on-surface-muted">- 09</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">September</span>{" "}
        <span className="text-on-surface-muted">- 09</span>
      </>
    ),
  },
  {
    value: "10",
    label: "October",
    renderOption: (
      <>
        <span className="font-medium">October</span>{" "}
        <span className="text-on-surface-muted">- 10</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">October</span>{" "}
        <span className="text-on-surface-muted">- 10</span>
      </>
    ),
  },
  {
    value: "11",
    label: "November",
    renderOption: (
      <>
        <span className="font-medium">November</span>{" "}
        <span className="text-on-surface-muted">- 11</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">November</span>{" "}
        <span className="text-on-surface-muted">- 11</span>
      </>
    ),
  },
  {
    value: "12",
    label: "December",
    renderOption: (
      <>
        <span className="font-medium">December</span>{" "}
        <span className="text-on-surface-muted">- 12</span>
      </>
    ),
    renderSelected: (
      <>
        <span className="font-medium">December</span>{" "}
        <span className="text-on-surface-muted">- 12</span>
      </>
    ),
  },
];

export const generateYearOptions = (
  count = 50,
  futureYears = 0,
): AutocompleteOption[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => {
    const year = (currentYear + futureYears - i).toString();
    return { value: year, label: year };
  });
};

export const generateEndYearOptions = (
  futureYears = 100,
): AutocompleteOption[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: futureYears + 1 }, (_, i) => {
    const year = (currentYear + i).toString();
    return { value: year, label: year };
  });
};

// ============================================================================
// Shared date display helpers
// ============================================================================

/** Short month labels for summary card date display */
export const MONTH_LABELS: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

/**
 * Format a date range with duration for summary card display.
 * Returns strings like "Oct 2024 - Present · 11 mo" or "May 2022 - Oct 2024 · 2 yr 6 mo".
 * Uses singular "yr"/"mo" (not "yrs"/"mos").
 */
export function formatDateRange(
  fromMonth: string = "",
  fromYear: string = "",
  toMonth: string = "",
  toYear: string = "",
  isCurrent: boolean,
): string | null {
  if (!fromMonth || !fromYear) return null;

  const fromLabel = MONTH_LABELS[fromMonth] || fromMonth;
  const fromDate = new Date(parseInt(fromYear), parseInt(fromMonth) - 1);

  const endDate = isCurrent
    ? new Date()
    : toMonth && toYear
      ? new Date(parseInt(toYear), parseInt(toMonth) - 1)
      : null;

  if (!endDate) return `${fromLabel} ${fromYear}`;

  const totalMonths = Math.max(0, differenceInMonths(endDate, fromDate));
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const durationParts: string[] = [];
  if (years > 0) durationParts.push(`${years} yr`);
  if (months > 0) durationParts.push(`${months} mo`);
  const duration = durationParts.join(" ") || "< 1 mo";

  if (isCurrent) {
    return `${fromLabel} ${fromYear} - Present · ${duration}`;
  }

  const toLabel = MONTH_LABELS[toMonth] || toMonth;
  return `${fromLabel} ${fromYear} - ${toLabel} ${toYear} · ${duration}`;
}
